// Silent migration of legacy person-scoped passkey credentials to
// user-scoped credentials. Re-encrypts every encrypted table under a fresh
// key derived from user.id and atomically updates the credential row.

import db, { beginEncryptedAccess, endEncryptedAccess } from '@/db/db';
import { getPasskeyCredential } from '@/db/services/credential_service';
import { getCurrentUser } from '@/db/services/user_service';
import { deriveEncryptionKey, bufferToHex, hexToBuffer } from './encryption';
import { createLogger, LogLevel } from './logger';

const logger = createLogger('PasskeyMigration', LogLevel.INFO);

/**
 * If the stored passkey credential is legacy person-scoped, re-encrypt all
 * encrypted tables under a new user-scoped key and update the credential
 * row in a single transaction. Safe to call on every unlock — it no-ops
 * when there's nothing to migrate.
 *
 * Crash-safety: the data writes and the credential update share one
 * transaction, so partial migrations roll back. Until that transaction
 * commits, the on-disk data is still old-key-encrypted and the stored
 * credential still describes the old key, so the next unlock works
 * exactly as before.
 *
 * @param rawId WebAuthn rawId from the assertion that just unlocked the app
 */
export async function maybeMigrateLegacyCredential(rawId: Uint8Array): Promise<void> {
  const credential = await getPasskeyCredential();
  if (!credential || credential.scope === 'user') {
    return;
  }

  const currentUser = await getCurrentUser();
  if (!currentUser) {
    logger.warn('Skipping passkey migration: no current user available');
    return;
  }

  logger.info('Migrating legacy person-scoped passkey to user-scoped');

  // 1. Read every encrypted table under the still-active old key. The
  // table list mirrors the encryption config in `beginEncryptedAccess`
  // (src/db/db.ts) — keep them in sync if a new encrypted table is added.
  // Note: the entire snapshot is held in memory at once. That's the cost
  // of the atomic-swap design (we need every row decrypted before we can
  // re-encrypt under the new key). For accounts with very large datasets
  // this could spike memory; if it becomes a problem the migration would
  // need to be chunked behind a key-wrapping scheme, which is a larger
  // architectural change.
  const snapshot = {
    clients: await db.clients.toArray(),
    contacts: await db.contacts.toArray(),
    documents: await db.documents.toArray(),
    applicationSession: await db.applicationSession.toArray(),
    signatures: await db.signatures.toArray(),
    caseNotes: await db.caseNotes.toArray(),
  };

  // 2. Derive both the new key (target state) and the old key (rollback
  // target). The old key is reproducible from the credential row because
  // `authenticatePasskey` uses the same recipe — rawId + stored salt +
  // stored userId — and the credential row hasn't been touched yet.
  const oldKey = await deriveEncryptionKey(
    rawId,
    hexToBuffer(credential.salt),
    credential.userId
  );
  const newSalt = crypto.getRandomValues(new Uint8Array(16));
  const newKey = await deriveEncryptionKey(rawId, newSalt, currentUser.id);

  // 3. Swap the encryption middleware to the new key, run the bulk-write
  // + credential-update transaction, and on ANY failure restore the old
  // key so the rest of the session keeps working. Without this revert a
  // mid-transaction error would leave the middleware keyed to newKey
  // while the on-disk data is still oldKey-encrypted, breaking every
  // subsequent read for the rest of the session.
  let succeeded = false;
  try {
    await endEncryptedAccess();
    await beginEncryptedAccess(newKey);

    await db.transaction(
      'rw',
      [
        db.clients,
        db.contacts,
        db.documents,
        db.applicationSession,
        db.signatures,
        db.caseNotes,
        db.biometricCredentials,
      ],
      async () => {
        await db.clients.bulkPut(snapshot.clients);
        await db.contacts.bulkPut(snapshot.contacts);
        await db.documents.bulkPut(snapshot.documents);
        await db.applicationSession.bulkPut(snapshot.applicationSession);
        await db.signatures.bulkPut(snapshot.signatures);
        await db.caseNotes.bulkPut(snapshot.caseNotes);

        await db.biometricCredentials.put({
          ...credential,
          userId: currentUser.id,
          scope: 'user',
          salt: bufferToHex(newSalt),
        });
      }
    );
    succeeded = true;
  } finally {
    if (!succeeded) {
      try {
        await endEncryptedAccess();
        await beginEncryptedAccess(oldKey);
      } catch (revertError) {
        logger.error(
          'Failed to revert encryption middleware to old key after migration failure:',
          revertError
        );
      }
    }
  }

  logger.info('Passkey migration complete');
}
